/**
 * /reclamaciones — Libro de Reclamaciones (Indecopi, Ley 29571).
 *
 * Mandatory in Peru for any e-commerce. Captures the consumer's identity,
 * the type of complaint, the facts and the requested resolution. On
 * submit we POST to ``/api/legal/complaints`` and show the assigned
 * receipt code so the user can quote it later.
 */

import { useState, type FormEvent } from 'react';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { LegalSection, LegalShell } from '@/components/layout/LegalShell';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';

interface FormState {
  document_type: 'DNI' | 'CE' | 'RUC' | 'Pasaporte';
  document_number: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  is_minor: boolean;
  kind: 'reclamo' | 'queja';
  product_type: 'producto' | 'servicio';
  product_description: string;
  amount_soles: string;
  facts: string;
  requested_resolution: string;
}

const EMPTY: FormState = {
  document_type: 'DNI',
  document_number: '',
  full_name: '',
  email: '',
  phone: '',
  address: '',
  is_minor: false,
  kind: 'reclamo',
  product_type: 'servicio',
  product_description: 'Suscripción / fierros de AlphaConcreto',
  amount_soles: '',
  facts: '',
  requested_resolution: '',
};

interface Receipt {
  id: number;
  receipt_code: string;
  created_at: string;
  message: string;
}

export function ComplaintsPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((s) => ({ ...s, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const amountCents = form.amount_soles
        ? Math.round(parseFloat(form.amount_soles) * 100)
        : 0;
      const { data } = await api.post<Receipt>('/api/legal/complaints', {
        document_type: form.document_type,
        document_number: form.document_number.trim(),
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        is_minor: form.is_minor,
        kind: form.kind,
        product_type: form.product_type,
        product_description: form.product_description.trim(),
        amount_cents: amountCents,
        facts: form.facts.trim(),
        requested_resolution: form.requested_resolution.trim(),
      });
      setReceipt(data);
    } catch (err) {
      let msg = 'No se pudo registrar el reclamo. Inténtalo de nuevo.';
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        const detail = err.response.data.detail;
        msg = typeof detail === 'string' ? detail : msg;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (receipt) {
    return (
      <LegalShell eyebrow="Libro de Reclamaciones" title="¡Reclamo registrado!">
        <div
          className="flex items-start gap-4 rounded-[var(--radius-md)] border p-5"
          style={{
            background: 'rgba(34,197,94,0.08)',
            borderColor: 'rgba(34,197,94,0.4)',
          }}
        >
          <CheckCircle2 size={28} strokeWidth={1.8} style={{ color: '#86efac' }} />
          <div>
            <div className="text-base" style={{ color: 'var(--text-primary)' }}>
              Hoja N° <strong>{receipt.receipt_code}</strong>
            </div>
            <p className="mt-2 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              {receipt.message}
            </p>
            <p className="mt-3 text-[12px]" style={{ color: 'var(--text-muted)' }}>
              Guarda este número como comprobante. Te enviamos también una copia al correo que
              proporcionaste.
            </p>
          </div>
        </div>
        <div className="mt-6">
          <Button variant="secondary" size="md" onClick={() => { setForm(EMPTY); setReceipt(null); }}>
            Registrar otro reclamo
          </Button>
        </div>
      </LegalShell>
    );
  }

  return (
    <LegalShell eyebrow="Indecopi · Ley 29571" title="Libro de Reclamaciones">
      <LegalSection title="Antes de empezar">
        <div className="flex items-start gap-3 rounded-[var(--radius-md)] border p-3"
          style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
        >
          <ShieldCheck size={18} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
          <div className="text-[13px]">
            <p>
              Conforme a la Ley 29571 (Código de Protección y Defensa del Consumidor),
              AlphaConcreto pone a tu disposición este libro de reclamaciones virtual.
            </p>
            <p className="mt-1.5" style={{ color: 'var(--text-muted)' }}>
              Te responderemos al correo proporcionado en un máximo de <strong>30 días
              calendario</strong>.
            </p>
          </div>
        </div>
      </LegalSection>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* ===== Consumidor ===== */}
        <FormGroup title="1. Identificación del consumidor">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label="Tipo de documento" required>
              <select
                value={form.document_type}
                onChange={(e) => update('document_type', e.currentTarget.value as FormState['document_type'])}
                className="h-10 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <option value="DNI">DNI</option>
                <option value="CE">Carné de Extranjería</option>
                <option value="RUC">RUC</option>
                <option value="Pasaporte">Pasaporte</option>
              </select>
            </Field>
            <Field label="Número" required className="sm:col-span-2">
              <Input
                value={form.document_number}
                onChange={(v) => update('document_number', v)}
                required
                maxLength={32}
              />
            </Field>
          </div>

          <Field label="Nombre completo" required>
            <Input value={form.full_name} onChange={(v) => update('full_name', v)} required maxLength={160} />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Correo electrónico" required>
              <Input
                type="email"
                value={form.email}
                onChange={(v) => update('email', v)}
                required
                maxLength={254}
              />
            </Field>
            <Field label="Teléfono">
              <Input value={form.phone} onChange={(v) => update('phone', v)} maxLength={32} />
            </Field>
          </div>

          <Field label="Dirección">
            <Input value={form.address} onChange={(v) => update('address', v)} maxLength={255} />
          </Field>

          <label className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={form.is_minor}
              onChange={(e) => update('is_minor', e.currentTarget.checked)}
              className="h-3.5 w-3.5 accent-[var(--accent)]"
            />
            El consumidor es menor de edad (requiere autorización de padres / tutores).
          </label>
        </FormGroup>

        {/* ===== Identificación del bien ===== */}
        <FormGroup title="2. Identificación del bien o servicio contratado">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Tipo" required>
              <select
                value={form.product_type}
                onChange={(e) => update('product_type', e.currentTarget.value as FormState['product_type'])}
                className="h-10 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 text-sm"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <option value="servicio">Servicio</option>
                <option value="producto">Producto</option>
              </select>
            </Field>
            <Field label="Monto reclamado (S/.)">
              <Input
                type="number"
                value={form.amount_soles}
                onChange={(v) => update('amount_soles', v)}
                min="0"
                step="0.01"
              />
            </Field>
          </div>
          <Field label="Descripción del bien / servicio" required>
            <Input value={form.product_description} onChange={(v) => update('product_description', v)} maxLength={255} />
          </Field>
        </FormGroup>

        {/* ===== Detalle ===== */}
        <FormGroup title="3. Detalle del reclamo o queja">
          <Field label="Tipo" required>
            <div className="flex gap-2">
              <RadioPill checked={form.kind === 'reclamo'} onClick={() => update('kind', 'reclamo')}>
                Reclamo (producto / servicio)
              </RadioPill>
              <RadioPill checked={form.kind === 'queja'} onClick={() => update('kind', 'queja')}>
                Queja (atención recibida)
              </RadioPill>
            </div>
          </Field>

          <Field label="Detalle de los hechos" required>
            <Textarea
              value={form.facts}
              onChange={(v) => update('facts', v)}
              rows={5}
              required
              minLength={10}
              maxLength={4000}
              placeholder="Describe con detalle lo ocurrido: fecha, qué pasó, qué intentaste, etc."
            />
          </Field>

          <Field label="Pedido del consumidor">
            <Textarea
              value={form.requested_resolution}
              onChange={(v) => update('requested_resolution', v)}
              rows={3}
              maxLength={2000}
              placeholder="¿Qué solución estás buscando? (reembolso, fierros, cambio de plan, etc.)"
            />
          </Field>
        </FormGroup>

        {error && (
          <div
            className="rounded-[var(--radius-sm)] border p-2 text-[12.5px]"
            style={{
              background: 'rgba(220,38,38,0.08)',
              borderColor: 'rgba(220,38,38,0.4)',
              color: '#fca5a5',
            }}
          >
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" size="md" disabled={busy}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {busy ? 'Enviando…' : 'Registrar reclamo'}
        </Button>
      </form>
    </LegalShell>
  );
}

// ---------------------------------------------------------------------------
// Form primitives
// ---------------------------------------------------------------------------

function FormGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset
      className="rounded-[var(--radius-md)] border p-5"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <legend
        className="px-2 text-[11px] uppercase tracking-[0.18em]"
        style={{ color: 'var(--accent)' }}
      >
        {title}
      </legend>
      <div className="mt-3 space-y-3">{children}</div>
    </fieldset>
  );
}

function Field({
  label,
  children,
  required,
  className = '',
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 text-[12px] ${className}`} style={{ color: 'var(--text-secondary)' }}>
      <span>
        {label} {required && <span style={{ color: 'var(--accent)' }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  type = 'text',
  required,
  maxLength,
  min,
  step,
}: {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  maxLength?: number;
  min?: string;
  step?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      required={required}
      maxLength={maxLength}
      min={min}
      step={step}
      className="h-10 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm"
      style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
    />
  );
}

function Textarea({
  value,
  onChange,
  rows = 4,
  required,
  minLength,
  maxLength,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      rows={rows}
      required={required}
      minLength={minLength}
      maxLength={maxLength}
      placeholder={placeholder}
      className="rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 py-2 text-sm"
      style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
    />
  );
}

function RadioPill({
  checked,
  onClick,
  children,
}: {
  checked: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[var(--radius-sm)] border px-3 py-2 text-sm transition-colors"
      style={{
        background: checked ? 'var(--accent)' : 'var(--surface-2)',
        borderColor: checked ? 'var(--accent)' : 'var(--border-default)',
        color: checked ? 'var(--surface-base)' : 'var(--text-secondary)',
      }}
    >
      {children}
    </button>
  );
}
